import { forwardRef } from "react"
import { AlertTriangle, RefreshCw } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { HotelOption } from "@/lib/api/types"
import { useBooking } from "@/context/BookingContext"
import { HotelCard } from "./HotelCard"
import { SectionHeader } from "./SectionHeader"

interface HotelSectionProps {
  onHotelSelect: (hotel: HotelOption) => void
  onOpenBookingRequest?: () => void
  onRetry?: () => void
}

export const HotelSection = forwardRef<HTMLElement, HotelSectionProps>(
  function HotelSection({ onHotelSelect, onOpenBookingRequest, onRetry }, ref) {
    const { state } = useBooking()
    const hotels = state.offers?.hotelOptions ?? []
    const passengerCount = state.passengerCount
    const hasError = state.offersStatus === "error"
    const sortedHotels = [...hotels].sort((a, b) => {
      if (a.soldOut !== b.soldOut) return a.soldOut ? 1 : -1
      if (a.priceDiffPerPerson === 0 && b.priceDiffPerPerson !== 0) return -1
      if (a.priceDiffPerPerson !== 0 && b.priceDiffPerPerson === 0) return 1
      const aMatches = a.roomCapacity === passengerCount
      const bMatches = b.roomCapacity === passengerCount
      if (aMatches && !bMatches) return -1
      if (!aMatches && bMatches) return 1
      return 0
    })

    return (
      <section ref={ref} className="scroll-mt-6 py-10">
        <SectionHeader
          step={1}
          title="Choose Your Hotel"
          subtitle="Live availability and price difference vs. base package"
        />

        {hasError ? (
          <div className="flex flex-col items-center gap-4 rounded-xl border border-border bg-card/80 px-6 py-12 text-center">
            <AlertTriangle className="size-8 text-amber-400" />
            <p className="text-sm text-muted-foreground">
              {state.offersError?.userMessage ?? "Hotels could not be loaded."}
            </p>
            {state.offersError?.isRetryable && onRetry && (
              <Button
                variant="outline"
                className="gap-2"
                onClick={onRetry}
              >
                <RefreshCw className="size-4" />
                Try again
              </Button>
            )}
          </div>
        ) : sortedHotels.length === 0 ? (
          <div className="rounded-xl border border-border bg-card/70 px-6 py-10 text-center text-sm text-muted-foreground">
            No hotel packages are available for the selected dates right now.
          </div>
        ) : (
          <div className="flex flex-col gap-4 @min-[48rem]:gap-5">
            {sortedHotels.map((hotel) => (
              <HotelCard
                key={`${hotel.allotmentId}:${hotel.hotelId}`}
                hotel={hotel}
                visuallySelected={
                  state.userSelectedHotel &&
                  state.selectedHotel !== null &&
                  state.selectedHotel.allotmentId === hotel.allotmentId &&
                  state.selectedHotel.hotelId === hotel.hotelId
                }
                isIncludedInPackage={
                  hotel.priceDiffPerPerson === 0 &&
                  state.selectedHotel?.allotmentId === hotel.allotmentId &&
                  state.selectedHotel?.hotelId === hotel.hotelId &&
                  !state.userSelectedHotel
                }
                currency={state.currency}
                selectedPriceDiffPerPerson={
                  state.selectedHotel?.priceDiffPerPerson ?? 0
                }
                onSelect={() => onHotelSelect(hotel)}
                onOpenBookingRequest={onOpenBookingRequest}
              />
            ))}
          </div>
        )}
      </section>
    )
  }
)
